import React, { useEffect, useMemo, useState } from 'react';
import {
  AddIcon,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Text,
  TextArea,
  VStack,
} from 'native-base';
import { MultilineInput } from '../Form/MultilineInput';
import { IconButton } from '../Form/IconButton';
import { DatePicker } from '../Form/DatePicker';
import { Select } from '../Form/Select';
import { format } from 'date-fns';
import { cleanMeetingNotes, MEETING_TYPES_SELECT_OPTIONS } from './utils';

const convertMeetingDateToInputFormat = (meetingDate) => {
  if (!meetingDate) return null;

  const dateObj = new Date(meetingDate);
  return format(dateObj, "yyyy-MM-dd'T'HH:mm");
};

export const MeetingContent = ({ isEditing, isLoading, meeting, onSave }) => {
  // fields
  const [link, setLink] = useState(meeting.notes_url ?? '');
  const [date, setDate] = useState(
    convertMeetingDateToInputFormat(meeting.meeting_date)
  );
  const [duration, setDuration] = useState(meeting.meeting_duration ?? '30');
  const [meetingType, setMeetingType] = useState(meeting.meeting_type);
  const [generalDescription, setGeneralDescription] = useState(
    meeting.notes ?? ''
  );
  const [highlights, setHighlights] = useState(meeting.highlights ?? []);

  // errors
  const [highlightValidationMessage, setHighlightValidationMessage] =
    useState('');

  const canAddNewHighlight = useMemo(() => {
    const isOneEmpty = highlights.some((h) => !h);
    return !isOneEmpty;
  }, [highlights]);

  useEffect(() => {
    if (canAddNewHighlight) {
      setHighlightValidationMessage('');
    }
  }, [canAddNewHighlight]);

  const onSaveMeetingNotes = () => {
    const urlWithProtocol = link.startsWith('http') ? link : `https://${link}`;
    const actualLink = !!link ? urlWithProtocol : link;
    const cleanHighlights = highlights.filter((h) => !!h);

    const updatedMeeting = {
      ...meeting,
      meeting_date: date,
      meeting_type: meetingType,
      meeting_duration: duration,
      notes_url: actualLink,
      highlights: cleanHighlights,
      notes: generalDescription,
    };

    // clean up of the component state in case of empty values
    setHighlights(cleanHighlights);
    onSave(cleanMeetingNotes(updatedMeeting));
  };

  return (
    <VStack pl={5} pr={7}>
      {isEditing && (
        <VStack>
          <Text fontSize={'xl'} fontWeight={'normal'} pb={3} textAlign="left">
            Time
          </Text>

          <HStack alignItems={'center'} maxW={'100%'} pb={3}>
            <DatePicker onChange={setDate} value={date} />
            <Input
              fontSize={'lg'}
              h={'46px'}
              marginBottom={0}
              ml={3}
              onChange={(e) => setDuration(e.target.value)}
              type={'text'}
              value={duration}
              w={'64px'}
            />
            <Text pl={3}>Min</Text>
          </HStack>

          <Text fontSize={'xl'} pb={3} textAlign="left" fontWeight={'normal'}>
            Type
          </Text>

          <Box width={'100%'}>
            <Select
              height={46}
              width={'100%'}
              maxWidth={'100%'}
              isSearchable={false}
              onChange={(opt) => setMeetingType(opt.value)}
              options={MEETING_TYPES_SELECT_OPTIONS}
              placeholder="Meeting Type"
              value={MEETING_TYPES_SELECT_OPTIONS.find(
                (opt) => opt.value === meetingType
              )}
            />
          </Box>

          <Text fontSize={'xl'} fontWeight={'normal'} py={3} textAlign="left">
            Link
          </Text>

          <Input
            fontSize={'lg'}
            maxW={'100%'}
            placeholder={'Specify a valid URL'}
            onChange={(e) => {
              setLink(e.target.value);
            }}
            type="text"
            value={link}
          />
        </VStack>
      )}

      <Text fontSize={'xl'} fontWeight={'normal'} pb={3} textAlign="left">
        General
      </Text>

      <VStack>
        {isEditing ? (
          <TextArea
            fontSize={'lg'}
            h={40}
            w={'100%'}
            maxW={'100%'}
            marginBottom={0}
            onChange={(e) => {
              setGeneralDescription(e.target.value);
            }}
            placeholder="Write some general notes..."
            type="text"
            value={generalDescription}
          />
        ) : (
          (generalDescription && (
            <Text fontSize={'lg'}>{generalDescription}</Text>
          )) || (
            <Text italic fontSize={'lg'}>
              No general notes
            </Text>
          )
        )}
      </VStack>

      <Flex alignItems={'center'} flexDir={'row'} mt={5}>
        <Text fontSize={'xl'} fontWeight={'normal'} textAlign="left" py={3}>
          Goals & Highlights
        </Text>

        {isEditing && (
          <IconButton
            onClick={() => {
              if (!canAddNewHighlight) {
                setHighlightValidationMessage(
                  'Please fill the empty input field before adding a new one.'
                );
                return;
              }

              setHighlights(['', ...highlights]);
            }}
          >
            <AddIcon />
          </IconButton>
        )}
      </Flex>

      {!!highlightValidationMessage && (
        <Text mb={2} fontSize={'sm'} color={'error.600'}>
          {highlightValidationMessage}
        </Text>
      )}

      {!isEditing && (
        <Box mb={4}>
          {highlights.length ? (
            highlights.map((goal, i) => (
              <Box
                backgroundColor={i % 2 === 0 ? 'muted.200' : 'muted.50'}
                key={i}
                p={1}
              >
                <Text fontSize={'lg'}>{goal}</Text>
              </Box>
            ))
          ) : (
            <Text italic fontSize={'lg'}>
              No Entries
            </Text>
          )}
        </Box>
      )}

      {isEditing && (
        <MultilineInput
          entries={highlights}
          onEntriesChange={(updatedHighlights) => {
            setHighlights(updatedHighlights);
          }}
          placeholder={'Specify a single goal or highlight'}
        />
      )}

      {isEditing && (
        <Flex
          alignItems={'center'}
          flexDir={'row'}
          justifyContent={'center'}
          mt={4}
          mb={4}
        >
          <Button
            h={'32px'}
            isDisabled={false}
            isLoading={isLoading}
            minH={'32px'}
            onPress={onSaveMeetingNotes}
            rounded={3}
            size="sm"
            type="button"
          >
            Save This Meeting Notes
          </Button>
        </Flex>
      )}
    </VStack>
  );
};
